import {
  FormGroup,
  InputGroup,
  Collapse,
  Dialog,
  DialogBody,
  DialogFooter,
  TextArea,
} from "@blueprintjs/core"
import {
  File,
  CaretDown,
  CaretUp,
  Ticket,
  Pencil,
  Info,
} from "@phosphor-icons/react"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import Divider from "components/Atoms/Divider"
import DynamicSelector from "components/Molecules/DynamicSelector"
import { PRODUCTION_LINE } from "graphql/selector.queries.gql"
import React, { useEffect, useState, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Pallet, { validatePallet } from "./Pallet"
import Note from "components/Atoms/Note"
import { Files } from "@phosphor-icons/react/dist/ssr"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { ADD_UPDATE_PRODUCTION } from "graphql/production/production.mutation.gql"
import { useProductionQuery } from "generated/graphql"
import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "store"
import PalletLogo from "assets/images/pallet.png"
import {
  GatePassStateType,
  MHCodeStateType,
  PalletStateType,
  resetAllGatePasses,
  resetAllMhCodes,
  setProduction,
} from "store/production.reducer"
import dayjs from "dayjs"
import { useCurrentDepartment } from "hooks/useAuth"
import { RESTOCK_PRODUCTION } from "graphql/production/production.queries.gql"
import PalletPackagingAction from "../actions/PalletPackagingAction"
import { ErrorToast } from "utils/AppToaster"

import PalletizationUserGuide from "./PalletizationUserGuide"

function safeValue<T>(
  value: T | null | undefined,
  fallback: string | number = "--"
) {
  if (value === null || value === undefined) return fallback
  if (typeof value === "string" && value.trim() === "") return fallback
  return value
}

const PalletizationDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [selectedLine, setSelectedLine] = useState<any | null>(null)
  const [wRefNo, setWRefNo] = useState<string>("")
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null)
  const [isEditOpen, toggleEdit] = useState(false)

  // Remark dialog states
  const [isRemarkDialogOpen, setIsRemarkDialogOpen] = useState(false)
  const [remarkAction, setRemarkAction] = useState<"verify" | "reject" | null>(
    null
  )
  const [remarkText, setRemarkText] = useState("")
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false)
  const [isDocsOpen, setIsDocsOpen] = useState(false)

  const isNewPalletization = id === "new"
  const [isProdOpen, setIsProdOpen] = useState(isNewPalletization)
  const dispatch = useDispatch()

  const departmentId = useCurrentDepartment()
  const [palletErrors, setPalletErrors] = useState<string[]>([])
  const [duplicatePalletError, setDuplicatePalletError] = useState<string>("")
  const { data, refetch } = useProductionQuery({
    variables: {
      id: id,
    },
    fetchPolicy: "network-only",
    onCompleted: d => {
      setSelectedLine(d?.production?.productionLine)
      setWRefNo(d?.production?.weeklySrNo)
    },
  })

  const production = data?.production

  const pallets = useSelector<ApplicationState, PalletStateType[]>(
    state => state.production.production.pallets
  )

  useEffect(() => {
    dispatch(setProduction({ id: null, pallets: [] }))
  }, [id, dispatch])

  useEffect(() => {
    if (production?.productionitemSet?.length) {
      const mapped: PalletStateType[] = [...production.productionitemSet]
        .sort((a, b) => (a?.sequenceNumber ?? 0) - (b?.sequenceNumber ?? 0))
        .map((p, idx) => ({
          id: p?.id ?? undefined,
          lotCode: p?.linepackingallotmentSet?.map(l => ({
            id: l?.id,
            date: dayjs(l?.createdAt).format("DD MMM YY"),
            lotCode: l?.lotCode,
            gatePass:
              l?.packingAllotment?.inwardAllocation?.inward?.booking?.schedule
                ?.gatePass ?? "",
            productionCreatedDate: l?.productionCreatedDate ?? "",
          })),
          date:
            p?.linepackingallotmentSet
              ?.map(l => `${dayjs(l?.createdAt).format("DD MMM 'YYYY")}`)
              .join(", ") ?? "",
          palletNumber: p?.palletNo ?? "",
          sequenceNumber: p?.sequenceNumber ?? idx + 1,
          mhCodes: (p?.linetradeprofileSet ?? []).map(m => ({
            id: m?.approvedTradeProfile?.id,
            responseId: m?.id,
            mhCode:
              m?.approvedTradeProfile?.tradeProfile?.certificateNumber ?? "",
            boxes: m?.assignedWt / production?.shipmentPlan?.skuType?.capacity,
            variety:
              m?.approvedTradeProfile?.tradeProfile?.plotCommodity?.variety
                ?.name ?? "",
            commodityName:
              m?.approvedTradeProfile?.tradeProfile?.plotCommodity?.variety
                ?.commodity?.name ?? "",
          })) as MHCodeStateType[],

          boxCount: p?.boxCount ?? 0,
          weightCount:
            p?.boxCount * production?.shipmentPlan?.skuType?.capacity,

          // Reversed (restocked) allotments come back with no weight and no
          // rejection - they are no longer assigned, so the gate pass must be
          // free to select again.
          gatePasses: (p?.linepackingallotmentSet ?? [])
            .filter(g => (g?.assignedWt ?? 0) > 0 || (g?.rejectionWt ?? 0) > 0)
            .map(g => ({
              id: g?.packingAllotment?.id,
              responseId: g?.id,
              gatePass:
                g?.packingAllotment?.inwardAllocation?.inward?.booking?.schedule
                  ?.gatePass ?? "",
              variety:
                g?.packingAllotment?.inwardAllocation?.inward?.booking?.schedule
                  ?.variety?.name ?? "",
              farmerName: [
                g?.packingAllotment?.inwardAllocation?.inward?.booking?.schedule
                  ?.plot?.farmer?.fullName,
                g?.packingAllotment?.inwardAllocation?.inward?.booking?.schedule
                  ?.plotCommodity?.registrationCode
                  ? `[${g.packingAllotment.inwardAllocation.inward.booking.schedule.plotCommodity.registrationCode}]`
                  : "",
              ]
                .filter(Boolean)
                .join(" ")
                .trim(),
              boxes:
                (g?.assignedWt - g?.rejectionWt) /
                production?.shipmentPlan?.skuType?.capacity,
              weight: g?.assignedWt - g?.rejectionWt,
              rejectedWeight: g?.rejectionWt,
              rejectedQuantity: g?.rejectionBoxCount,
            })) as GatePassStateType[],

          remark: p?.remark ?? "",
          isEuro: p?.palletType === "EURO",
          size: (() => {
            try {
              return (p?.metadata ? JSON.parse(p.metadata) : {})?.size ?? ""
            } catch {
              return ""
            }
          })(),
        }))

      dispatch(setProduction({ id: production.id, pallets: mapped }))
    }
  }, [production, dispatch])

  function mapPalletsToItems(pallets: PalletStateType[], production: any) {
    const qtyPerBox = production?.shipmentPlan?.skuType?.capacity ?? 1

    return pallets.map(p => ({
      id: p?.id,
      palletNo: p.palletNumber,
      // Always send a clean integer to the backend — floating-point drift
      // from per-GP box rounding (e.g. 124.9966...) must not reach the mutation.
      boxCount: Math.round(p.boxCount),
      palletType: p.isEuro ? "EURO" : "NORMAL",
      remark: p.remark ?? "",
      metadata: { size: p.size ?? "" },
      sequenceNumber: p.sequenceNumber,
      lineTradeProfiles: p.mhCodes.map(m => ({
        id: m?.responseId,
        approvedTradeProfileId: m.id,
        assignedWt: ((m.boxes ?? 0) * qtyPerBox).toString(),
      })),
      linePackingAllotments: p.gatePasses.map(g => ({
        id: g?.responseId,
        packingAllotmentId: g.id,
        assignedWt: g.weight?.toString() ?? "0",
        rejectionWt: g.rejectedWeight?.toString() ?? "0",
      })),
    }))
  }

  const palletRefs = React.useRef<(HTMLDivElement | null)[]>([])

  const prevPalletsRef = useRef<PalletStateType[]>([])

  useEffect(() => {
    if (!pallets?.length) return

    // Check if only pallet numbers changed (no MH codes or gate passes changed)
    const onlyPalletNumberChanged = pallets.every((pallet, idx) => {
      const prevPallet = prevPalletsRef.current[idx]
      if (!prevPallet) return false

      const mhCodesEqual =
        JSON.stringify(pallet.mhCodes) === JSON.stringify(prevPallet.mhCodes)
      const gatePassesEqual =
        JSON.stringify(pallet.gatePasses) ===
        JSON.stringify(prevPallet.gatePasses)

      return mhCodesEqual && gatePassesEqual
    })

    // Update the ref for next comparison
    prevPalletsRef.current = pallets

    // Skip scrolling if only pallet numbers changed
    if (
      onlyPalletNumberChanged &&
      prevPalletsRef.current.length === pallets.length
    )
      return

    const meta = production?.shipmentPlan?.metadata
      ? JSON.parse(production.shipmentPlan.metadata)
      : {}

    const capacity = production?.shipmentPlan?.skuType?.capacity ?? 0

    const mhIncompleteIndex = pallets.findIndex(pallet => {
      const requiredBoxes = pallet.isEuro
        ? meta.euro_pallet_box_count ?? 0
        : meta.regular_pallet_box_count ?? 0

      const totalBoxes = pallet.mhCodes?.reduce(
        (sum, mh) => sum + (mh.boxes ?? 0),
        0
      )

      return !pallet.mhCodes?.length || totalBoxes !== requiredBoxes
    })

    if (mhIncompleteIndex !== -1 && palletRefs.current[mhIncompleteIndex]) {
      palletRefs.current[mhIncompleteIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
      setHighlightIndex(mhIncompleteIndex)

      setTimeout(() => setHighlightIndex(null), 3000)
      return
    }

    const gatePassIncompleteIndex = pallets.findIndex(pallet => {
      const totalBoxes = pallet.mhCodes?.reduce(
        (sum, mh) => sum + (mh.boxes ?? 0),
        0
      )
      const totalWeight = pallet.gatePasses?.reduce(
        (sum, gp) => sum + (gp.weight ?? 0),
        0
      )

      const requiredWeight = totalBoxes * capacity

      return (
        !pallet.gatePasses?.length ||
        totalWeight === 0 ||
        totalWeight !== requiredWeight
      )
    })
    if (
      gatePassIncompleteIndex !== -1 &&
      palletRefs.current[gatePassIncompleteIndex]
    ) {
      palletRefs.current[gatePassIncompleteIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
      setHighlightIndex(gatePassIncompleteIndex)

      setTimeout(() => setHighlightIndex(null), 3000)
    }
  }, [pallets])

  const sameLine = production?.productionLine?.id === selectedLine?.id
  const isVerified = production?.metadata
    ? JSON.parse(production.metadata)?.status === "verify"
    : false

  return (
    <CustomCard className={"m-4"}>
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center">
          <span>
            <CustomButton
              icon="arrow-left"
              onClick={() => navigate("/d/allocation/palletization")}
              minimal
            />
          </span>
          <h5 className="ml-3">Pallets</h5>
        </div>
        <CustomButton
          minimal
          icon={<Info size={20} />}
          onClick={() => setIsDocsOpen(true)}
          // title="Developer / Handover Documentation"
        />
      </div>
      <Divider className="w-full mt-0" />

      <div className="mx-20 mt-3 ">
        <section className="bg-blue-50 p-4 rounded-lg border border-slate-300 sticky top-16 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <File size={20} />
              <span className="text-gray-800 font-700">Production Details</span>

              {!isProdOpen && !isNewPalletization && (
                <CustomTag minimal intent="primary" className="p-2 rounded-lg">
                  <div className="flex flex-wrap items-center gap-2 text-gray-800">
                    <span>
                      <b>SO</b> {safeValue(production?.shipmentPlan?.soNo)}
                    </span>
                    <span className="text-gray-400">|</span>
                    <span>
                      <b>Customer Name</b>{" "}
                      {safeValue(
                        production?.shipmentPlan?.exportCustomer?.name
                      )}
                    </span>
                    <span className="text-gray-400">|</span>
                    <span>
                      <b>Color Group</b>{" "}
                      {safeValue(production?.shipmentPlan?.colourGroup?.name)}
                    </span>
                    <span className="text-gray-400">|</span>
                    <span>
                      <b>Packing Type</b>{" "}
                      <CustomTag
                        intent={
                          production?.shipmentPlan?.varietyType === "MIX"
                            ? "primary"
                            : "warning"
                        }
                        minimal
                      >
                        {safeValue(production?.shipmentPlan?.varietyType)}
                      </CustomTag>
                    </span>
                    <span className="text-gray-400">|</span>
                    <span>
                      <b>Packing Format</b>{" "}
                      {safeValue(production?.shipmentPlan?.skuType?.name)}
                    </span>

                    <span className="text-gray-400">|</span>
                    <span>
                      <b>Variety</b>{" "}
                      {safeValue(
                        production?.shipmentPlan?.varieties
                          ?.map(v => v?.name)
                          ?.join(", ")
                      )}
                    </span>
                  </div>
                </CustomTag>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Status Badge */}
              {!isNewPalletization &&
                (() => {
                  const metadata = production?.metadata
                    ? JSON.parse(production.metadata)
                    : null
                  const status = metadata?.status

                  if (status) {
                    return (
                      <CustomTag
                        intent={status === "verify" ? "success" : "danger"}
                        className="cursor-pointer"
                        onClick={() => setIsTimelineDialogOpen(true)}
                      >
                        {status === "verify" ? "✓ Verified" : "✗ Rejected"}
                      </CustomTag>
                    )
                  }
                  return null
                })()}

              <Pencil
                size={18}
                onClick={() => toggleEdit(true)}
                className="cursor-pointer"
              />
              <PalletPackagingAction
                isOpen={isEditOpen}
                onClose={() => {
                  toggleEdit(false)
                  refetch()
                }}
                soId={production?.shipmentPlan?.id}
              />
              {isProdOpen ? (
                <CaretUp
                  onClick={() => setIsProdOpen(false)}
                  size={18}
                  className="cursor-pointer"
                />
              ) : (
                <CaretDown
                  onClick={() => setIsProdOpen(true)}
                  size={18}
                  className="cursor-pointer"
                />
              )}
            </div>
          </div>

          <Collapse isOpen={isProdOpen}>
            <div className="mt-4 grid grid-cols-12 gap-4 border-dashed border-slate-300 border p-4 rounded-md bg-white">
              <div className="col-span-4">
                <FormGroup
                  className="mb-5"
                  label={
                    <>
                      Line <span className="text-red">*</span>
                    </>
                  }
                  disabled={isVerified}
                >
                  <DynamicSelector
                    required
                    helperText={
                      !isNewPalletization &&
                      !isVerified && (
                        <>
                          {" "}
                          To change the line, select a new one and click
                          Confirm.
                        </>
                      )
                    }
                    query={PRODUCTION_LINE}
                    onChange={(_, e) => setSelectedLine(e)}
                    value={selectedLine}
                    variables={{ departmentId: departmentId }}
                    className="mb-5"
                  />
                </FormGroup>
              </div>

              <div className="col-span-4">
                <FormGroup
                  className="mb-5"
                  disabled={!isNewPalletization}
                  label={
                    <>
                      W Ref. No. <span className="text-red">*</span>
                    </>
                  }
                >
                  <InputGroup
                    disabled={!isNewPalletization}
                    required
                    value={wRefNo}
                    onChange={e => setWRefNo(e.target.value)}
                  />
                </FormGroup>
              </div>

              {/* {(isNewPalletization || !hasAnyGatePass) && ( */}
              <div className="col-span-4 mt-6">
                <div className="flex gap-2">
                  <ButtonMutation
                    mutation={ADD_UPDATE_PRODUCTION}
                    variables={{
                      input: {
                        id: production?.id == "new" ? null : production?.id,
                        productionLineId: selectedLine?.id,
                        weeklySrNo: wRefNo,
                      },
                    }}
                    intent="success"
                    text="Confirm"
                    dark={false}
                    onCompleted={e => {
                      console.log(e)
                      const newId = e?.addUpdateProduction?.production?.id
                      if (newId) {
                        navigate(`/d/allocation/palletization/${newId}`)
                      } else {
                        ErrorToast(`${e?.addUpdateProduction?.error}`)
                      }
                      setTimeout(() => refetch(), 100)
                    }}
                  />
                </div>
              </div>
              {/* )} */}
            </div>

            {!isNewPalletization && (
              <div className="grid grid-cols-4 gap-y-3 gap-x-8 text-xs mt-6">
                <div className="space-y-3">
                  <p>
                    <b>Sales Order no. (SO):</b> <br />{" "}
                    {safeValue(production?.shipmentPlan?.soNo)}
                  </p>
                  <p>
                    <b>Variety:</b> <br />
                    {safeValue(
                      production?.shipmentPlan?.varieties
                        ?.map(v => v?.name)
                        ?.join(", ")
                    )}
                  </p>
                  <p>
                    <b>Label Type:</b> <br />{" "}
                    {safeValue(production?.shipmentPlan?.labels)}
                  </p>
                </div>
                <div className="space-y-3">
                  <p>
                    <b>Customer Name:</b> <br />{" "}
                    {safeValue(production?.shipmentPlan?.exportCustomer?.name)}
                  </p>
                  <p>
                    <b>Packing Format (SKU):</b>
                    <br /> {safeValue(production?.shipmentPlan?.skuType?.name)}
                  </p>
                </div>
                <div className="space-y-3">
                  <p>
                    <b>Color Group:</b> <br />{" "}
                    {safeValue(production?.shipmentPlan?.colourGroup?.name)}
                  </p>
                  <p>
                    <b>Primary Packing:</b> <br />{" "}
                    {safeValue(
                      production?.shipmentPlan?.primaryPackMaterial?.name
                    )}
                  </p>
                </div>
                <div className="space-y-3">
                  <p>
                    <b>Packing Type:</b>
                    <br />{" "}
                    <CustomTag
                      intent={
                        production?.shipmentPlan?.varietyType === "MIX"
                          ? "primary"
                          : "warning"
                      }
                      round
                      minimal
                    >
                      {safeValue(production?.shipmentPlan?.varietyType)}
                    </CustomTag>
                  </p>

                  <p>
                    <b>Secondary Packing Material:</b> <br />{" "}
                    {safeValue(
                      production?.shipmentPlan?.secondaryPackMaterial?.name
                    )}
                  </p>
                </div>
              </div>
            )}
          </Collapse>
        </section>

        {!isNewPalletization && sameLine && (
          <section>
            <div className="flex items-center justify-between mt-6 mb-3 bg-white py-2 -mx-20 px-20 sticky top-32 z-10">
              <h6 className="font-600">Pallets</h6>
              <div className="flex items-center">
                <Note intent="success" className="mr-4">
                  <span className="flex items-center gap-2 text-sm text-black">
                    <Files />
                    <b>MH Codes:</b>{" "}
                    {(() => {
                      const mhSummary = pallets
                        .flatMap(p => p.mhCodes ?? [])
                        .reduce<Record<string, number>>((acc, m) => {
                          if (!m.mhCode) return acc
                          acc[m.mhCode] = (acc[m.mhCode] || 0) + (m.boxes ?? 0)
                          return acc
                        }, {})

                      const mhCodes = Object.keys(mhSummary)

                      return mhCodes.length
                        ? `${mhCodes.length} | ${mhCodes.join(", ")}`
                        : "--"
                    })()}
                    {" | "}
                    <Ticket />
                    <b>Gate Passes:</b>{" "}
                    {(() => {
                      const gpSummary = pallets
                        .flatMap(p => p.gatePasses ?? [])
                        .reduce<Record<string, number>>((acc, g) => {
                          if (!g.gatePass) return acc
                          acc[g.gatePass] =
                            (acc[g.gatePass] || 0) + (g.boxes ?? 0)
                          return acc
                        }, {})

                      const gpCodes = Object.keys(gpSummary)

                      return gpCodes.length
                        ? `${gpCodes.length} | ${gpCodes.join(", ")}`
                        : "--"
                    })()}
                  </span>
                </Note>
              </div>
            </div>
            <Divider className="w-full mt-0 mb-4" />

            {pallets.length > 0 ? (
              pallets.map((pallet, idx) => (
                <div
                  key={idx}
                  ref={el => (palletRefs.current[idx] = el)}
                  className="mt-4"
                  style={{
                    border:
                      highlightIndex === idx
                        ? "2px solid red"
                        : "1px solid transparent",
                    borderRadius: "12px",
                    transition: "border 0.3s ease",
                  }}
                >
                  <Pallet
                    key={idx}
                    index={idx}
                    data={pallet}
                    production={production}
                    onRefetch={refetch}
                  />
                </div>
              ))
            ) : (
              <Pallet
                index={0}
                data={{
                  palletNumber: "",
                  sequenceNumber: 1,
                  mhCodes: [],
                  boxCount: 0,
                  weightCount: 0,
                  gatePasses: [],
                  remark: "",
                  isEuro: false,
                }}
                production={production}
                onRefetch={refetch}
              />
            )}
          </section>
        )}

        <hr />

        {isNewPalletization && (
          <section>
            <div className="flex justify-center items-center">
              <img src={PalletLogo} width={400} height={400} alt="" />
            </div>
          </section>
        )}

        <div className="space-y">
          {palletErrors.length > 0 && (
            <div className="mt-2 text-red-500 font-bold flex justify-end gap-2 items-center">
              ⚠ Please add all fields in the following pallets:{" "}
              {palletErrors.join(", ")}
            </div>
          )}
          {!isNewPalletization && sameLine && (
            <div className="flex justify-between items-center">
              {(() => {
                const items = production?.productionitemSet ?? []

                if (items.length === 0) {
                  return (
                    <div className="flex gap-2 items-center">
                      <CustomButton onClick={() => dispatch(resetAllMhCodes())}>
                        Reset MhCodes
                      </CustomButton>
                      <CustomButton
                        onClick={() => dispatch(resetAllGatePasses())}
                      >
                        Reset Gate Passes
                      </CustomButton>
                    </div>
                  )
                }

                const allMhCodesEmpty = items.every(
                  item => (item?.linetradeprofileSet?.length ?? 0) === 0
                )

                const allGatePassesEmpty = items.every(
                  item => (item?.linepackingallotmentSet?.length ?? 0) === 0
                )

                return (
                  <div className="flex gap-2 items-center">
                    {allMhCodesEmpty && (
                      <CustomButton onClick={() => dispatch(resetAllMhCodes())}>
                        Reset MhCodes
                      </CustomButton>
                    )}
                    {allGatePassesEmpty && (
                      <CustomButton
                        onClick={() => dispatch(resetAllGatePasses())}
                      >
                        Reset Gate Passes
                      </CustomButton>
                    )}
                  </div>
                )
              })()}

              {(() => {
                const items = production?.productionitemSet ?? []
                const isSendForApproval = production?.sendForApproval

                if (items.length > 0 && !isSendForApproval) {
                  return (
                    <div className="flex gap-2 items-center">
                      {production?.productionitemSet?.flatMap(
                        item =>
                          item?.linetradeprofileSet?.map(
                            profile => profile?.id
                          ) ?? []
                      ).length > 0 && (
                        <ButtonMutation
                          mutation={RESTOCK_PRODUCTION}
                          variables={{
                            input: {
                              lineTradeProfileIds:
                                production?.productionitemSet?.flatMap(
                                  item =>
                                    item?.linetradeprofileSet?.map(
                                      profile => profile?.id
                                    ) ?? []
                                ),
                            },
                          }}
                          onCompleted={() => {
                            refetch()
                          }}
                        >
                          Restock MhCodes
                        </ButtonMutation>
                      )}

                      {production?.productionitemSet?.flatMap(
                        item =>
                          item?.linepackingallotmentSet?.map(p => p?.id) ?? []
                      ).length > 0 && (
                        <ButtonMutation
                          mutation={RESTOCK_PRODUCTION}
                          onCompleted={() => {
                            refetch()
                          }}
                          variables={{
                            input: {
                              linePackingAllotmentIds:
                                production?.productionitemSet?.flatMap(
                                  item =>
                                    item?.linepackingallotmentSet?.map(
                                      p => p?.id
                                    ) ?? []
                                ),
                            },
                          }}
                        >
                          Restock Gate Passes
                        </ButtonMutation>
                      )}
                    </div>
                  )
                }
              })()}

              {!production?.sendForApproval && (
                <div className="flex flex-col items-end gap-2">
                  {duplicatePalletError && (
                    <div className="text-red-500 font-bold text-sm">
                      ⚠ {duplicatePalletError}
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <ButtonMutation
                      mutation={ADD_UPDATE_PRODUCTION}
                      intent="primary"
                      dark={false}
                      disabled={production?.sendForApproval}
                      validate={() => {
                        // Check for duplicate pallet numbers
                        const palletNumbers = pallets
                          .map(p => p.palletNumber)
                          .filter(Boolean)

                        const duplicates = palletNumbers.filter(
                          (num, idx) => palletNumbers.indexOf(num) !== idx
                        )

                        if (duplicates.length > 0) {
                          setDuplicatePalletError(
                            `Duplicate pallet numbers found: ${[
                              ...new Set(duplicates),
                            ].join(
                              ", "
                            )}. Each pallet must have a unique number.`
                          )
                          return false
                        }

                        setDuplicatePalletError("")
                        return true
                      }}
                      variables={{
                        input: {
                          id: production?.id,
                          productionLineId: selectedLine?.id,
                          weeklySrNo: wRefNo,
                          sendForApproval: false,
                          items: mapPalletsToItems(pallets, production),
                        },
                      }}
                      onCompleted={() => {
                        refetch()
                      }}
                      onError={({ graphQLErrors, networkError }) => {
                        graphQLErrors.forEach(({ message }) => {
                          // Check if it's a duplicate pallet number error
                          if (message.includes("unique_production_pallet_no")) {
                            // Extract pallet number from error message
                            const match = message.match(
                              /\(production_id, pallet_no\)=\([^,]+,\s*(\d+)\)/
                            )
                            const palletNo = match ? match[1] : "unknown"

                            setDuplicatePalletError(
                              `Pallet number ${palletNo} already exists in this production. Please use a different pallet number.`
                            )
                          } else {
                            setDuplicatePalletError(
                              message ||
                                "Something went wrong. Please try again."
                            )
                          }
                        })

                        if (networkError) {
                          setDuplicatePalletError(networkError.message)
                        }
                      }}
                    >
                      Save
                    </ButtonMutation>

                    <ButtonMutation
                      mutation={ADD_UPDATE_PRODUCTION}
                      intent="success"
                      disabled={
                        production?.productionitemSet?.length === 0 ||
                        production?.shipmentPlan?.skuFilled > pallets?.length ||
                        production?.sendForApproval
                      }
                      dark={false}
                      validate={() => {
                        const errorsPerPallet = pallets.map(p => {
                          const errors = validatePallet(p, production)
                          return {
                            palletNumber: p.palletNumber || p.sequenceNumber,
                            errors,
                          }
                        })

                        const palletsWithErrors = errorsPerPallet
                          .filter(pe => pe.errors.length > 0)
                          .map(pe => pe.palletNumber.toString())

                        setPalletErrors(palletsWithErrors)

                        if (palletsWithErrors.length > 0) {
                          return false
                        }
                        return true
                      }}
                      variables={{
                        input: {
                          id: production?.id,
                          productionLineId: selectedLine?.id,
                          weeklySrNo: wRefNo,
                          sendForApproval: true,
                        },
                      }}
                      onCompleted={() => {
                        navigate("/d/allocation/palletization")
                      }}
                    >
                      Send For Approval
                    </ButtonMutation>
                  </div>
                </div>
              )}

              {!isNewPalletization && production?.sendForApproval && (
                <div className="flex justify-end gap-2 items-center">
                  {(() => {
                    // Safely parse metadata
                    const metadata = production?.metadata
                      ? JSON.parse(production.metadata)
                      : null
                    const status = metadata?.status

                    // Case 1: No status → show both Verify & Reject
                    if (!status) {
                      return (
                        <>
                          <CustomButton
                            intent="danger"
                            onClick={() => {
                              setRemarkAction("reject")
                              setRemarkText("")
                              setIsRemarkDialogOpen(true)
                            }}
                            className="flex items-center gap-2"
                          >
                            <span>✗</span> Reject
                          </CustomButton>

                          <CustomButton
                            intent="success"
                            onClick={() => {
                              setRemarkAction("verify")
                              setRemarkText("")
                              setIsRemarkDialogOpen(true)
                            }}
                            className="flex items-center gap-2"
                          >
                            <span>✓</span> Verify
                          </CustomButton>
                        </>
                      )
                    }

                    // Case 2: Rejected → show only Verify
                    if (status === "reject") {
                      return (
                        <CustomButton
                          intent="success"
                          onClick={() => {
                            setRemarkAction("verify")
                            setRemarkText("")
                            setIsRemarkDialogOpen(true)
                          }}
                          className="flex items-center gap-2"
                        >
                          <span>✓</span> Verify
                        </CustomButton>
                      )
                    }

                    // Case 3: Verified → show nothing
                    return null
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Remark Dialog */}
      <Dialog
        isOpen={isRemarkDialogOpen}
        onClose={() => {
          setIsRemarkDialogOpen(false)
          setRemarkText("")
          setRemarkAction(null)
        }}
        title={
          remarkAction === "verify" ? "Verify Production" : "Reject Production"
        }
        className="max-w-md"
      >
        <DialogBody>
          <FormGroup
            label="Remark"
            helperText="Please provide a remark for this action"
          >
            <TextArea
              fill
              rows={4}
              value={remarkText}
              onChange={e => setRemarkText(e.target.value)}
              placeholder="Enter your remark here..."
            />
          </FormGroup>
        </DialogBody>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <CustomButton
              onClick={() => {
                setIsRemarkDialogOpen(false)
                setRemarkText("")
                setRemarkAction(null)
              }}
            >
              Cancel
            </CustomButton>
            <ButtonMutation
              mutation={ADD_UPDATE_PRODUCTION}
              intent={remarkAction === "verify" ? "success" : "danger"}
              dark={false}
              variables={{
                input: {
                  id: production?.id,
                  productionLineId: selectedLine?.id,
                  weeklySrNo: wRefNo,
                  sendForApproval: remarkAction === "reject" ? false : true,
                  metadata: (() => {
                    const currentMetadata = production?.metadata
                      ? JSON.parse(production.metadata)
                      : {}
                    const remarkHistory = currentMetadata.remarkHistory || []

                    // Append new remark to history
                    remarkHistory.push({
                      status: remarkAction,
                      remark: remarkText,
                      date: new Date().toISOString(),
                    })

                    return {
                      status: remarkAction,
                      remarkHistory: remarkHistory,
                    }
                  })(),
                },
              }}
              onCompleted={() => {
                setIsRemarkDialogOpen(false)
                setRemarkText("")
                setRemarkAction(null)
                navigate("/d/allocation/palletization")
              }}
            >
              Confirm
            </ButtonMutation>
          </div>
        </DialogFooter>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog
        isOpen={isTimelineDialogOpen}
        onClose={() => setIsTimelineDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <span>Remark History</span>
          </div>
        }
        className="max-w-3xl"
      >
        <DialogBody className="max-h-[70vh] overflow-y-auto bg-gray-50">
          {(() => {
            const metadata = production?.metadata
              ? JSON.parse(production.metadata)
              : null
            const remarkHistory = metadata?.remarkHistory || []

            if (remarkHistory.length === 0) {
              return (
                <div className="text-center text-gray-500 py-12">
                  <div className="text-4xl mb-3">📝</div>
                  <div className="text-lg font-medium">
                    No remark history available
                  </div>
                  <div className="text-sm mt-2">
                    Remarks will appear here once actions are taken
                  </div>
                </div>
              )
            }

            // Show newest first
            const sortedHistory = [...remarkHistory].reverse()

            return (
              <div className="space-y-6 p-4">
                {sortedHistory.map((entry: any, idx: number) => (
                  <div key={idx} className="flex gap-5">
                    {/* Timeline connector */}
                    <div className="flex flex-col items-center pt-1">
                      <div
                        className={`w-4 h-4 rounded-full border-2 ${
                          entry.status === "verify"
                            ? "bg-green-500 border-green-600 shadow-lg shadow-green-200"
                            : "bg-red-500 border-red-600 shadow-lg shadow-red-200"
                        }`}
                      />
                      {idx < sortedHistory.length - 1 && (
                        <div
                          className="w-0.5 h-full bg-gray-300 mt-2"
                          style={{ minHeight: "60px" }}
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-2">
                      <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">
                                {entry.status === "verify" ? "✓" : "✗"}
                              </span>
                              <span
                                className={`font-semibold text-base ${
                                  entry.status === "verify"
                                    ? "text-green-700"
                                    : "text-red-700"
                                }`}
                              >
                                {entry.status === "verify"
                                  ? "Verified"
                                  : "Rejected"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>🕒</span>
                              <span>
                                {dayjs(entry.date).format(
                                  "DD MMM YYYY, hh:mm A"
                                )}
                              </span>
                            </div>
                          </div>
                          <div
                            className="text-sm text-gray-800 bg-gray-50 p-3 rounded border-l-4 ${
                            entry.status === 'verify' ? 'border-green-500' : 'border-red-500'
                          }"
                          >
                            <div className="font-medium text-gray-600 text-xs mb-1">
                              Remark:
                            </div>
                            <div className="leading-relaxed">
                              {entry.remark}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </DialogBody>
        <DialogFooter className="bg-gray-50 flex justify-end w-full">
          <CustomButton
            onClick={() => setIsTimelineDialogOpen(false)}
            intent="primary"
          >
            Close
          </CustomButton>
        </DialogFooter>
      </Dialog>

      <PalletizationUserGuide
        isOpen={isDocsOpen}
        onClose={() => setIsDocsOpen(false)}
      />
    </CustomCard>
  )
}

export default PalletizationDetail
